import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { Store } from 'redux';
import { of, Subject, Subscription } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    switchMap,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import {
    CmpsForm,
    CmpsFormBuilder,
    CmpsFormField,
    CmpsFormFields,
} from '../common/experimental.form';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';

import { defaultFormValue } from './form.tdcf.model';
import { formSave, formSetReadonly, formValueChange } from './tdcf-form.action';
import { TranslationKeyMap } from './tdcf-form.i18n';
import { FormState, storeStateMapper } from './tdcf-form.state';
import {
    Args,
    FlowState,
    HierarchyInfo,
    isWithinCurrentDateRange,
    Mode,
    PayloadExtended,
    PayloadView,
    ReviewDateRange,
} from './tdcf.model';

interface FormFieldsKey extends CmpsFormFields {
    LOOKING_BACK: CmpsFormField;
    LOOKING_FORWARD: CmpsFormField;
    CONVERSATION_SUMMARY: CmpsFormField;
}

const MAX_LENGTH = 2000;

const FormFields: FormFieldsKey = {
    LOOKING_BACK: {
        id: `lookingBack${Math.random()}`,
        name: 'lookingBack',
        labelId: `lookingBack${Math.random()}`,
        label: 'form_lookingBackLabel',
        placeholder: 'form_lookingBackPlaceholder',
        validators: [Validators.maxLength(MAX_LENGTH)],
        validationMsgs: {
            maxlength:
                TranslationKeyMap._7227__FIELD_LABEL__MUST_BE_LESS_THAN_OR_EQUAL_TO__REQUIREDLENGTH__CHARACTERS_,
        },
    },
    LOOKING_FORWARD: {
        id: `lookingForward${Math.random()}`,
        name: 'lookingForward',
        labelId: `lookingForward${Math.random()}`,
        label: 'form_lookingForwardLabel',
        placeholder: 'form_lookingForwardPlaceholder',
        validators: [Validators.maxLength(MAX_LENGTH)],
        validationMsgs: {
            maxlength:
                TranslationKeyMap._7227__FIELD_LABEL__MUST_BE_LESS_THAN_OR_EQUAL_TO__REQUIREDLENGTH__CHARACTERS_,
        },
    },
    CONVERSATION_SUMMARY: {
        id: `conversationSummary${Math.random()}`,
        name: 'conversationSummary',
        labelId: `conversationSummary${Math.random()}`,
        label: 'form_conversationSummaryLabel',
        placeholder: 'form_conversationSummaryPlaceholder',
        validators: [Validators.maxLength(MAX_LENGTH)],
        validationMsgs: {
            maxlength:
                TranslationKeyMap._7227__FIELD_LABEL__MUST_BE_LESS_THAN_OR_EQUAL_TO__REQUIREDLENGTH__CHARACTERS_,
        },
    },
};

@Component({
    selector: '[cmpsTdcfForm]',
    templateUrl: './form.tdcf.component.html',
})
export class FormTdcfComponent
    implements OnInit, OnDestroy, StateToProps<FormState>
{
    @Input() mode: Mode;
    @Input() templateId: number;
    @Input() payload: PayloadView;
    @Input() info: HierarchyInfo;
    @Input() reviewDateRanges: ReviewDateRange[];
    @Output() payloadChange: EventEmitter<PayloadExtended>;

    i18n = TranslationKeyMap;
    FlowState = FlowState;
    MAX_LENGTH = MAX_LENGTH;
    private _key: string;
    form: CmpsForm<FormFieldsKey>;
    private _storeSub: Subscription;
    state: FormState;
    private _formSub: Subscription;
    private _draftSubj: Subject<any>;
    private _draftSub: Subscription;
    isWithinCurrentDateRange: boolean;
    isMember: boolean;
    isManager: boolean;
    isMemberSelf: boolean;
    memberCanEdit: boolean;
    managerCanEdit: boolean;
    memberCanSubmit: boolean;
    managerCanSubmit: boolean;
    private _didSubmit: boolean;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        @Inject(STORE) private readonly _store: Store,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _authService: AuthService,
    ) {
        this.payloadChange = new EventEmitter();
        this.form = this._formBuilder.create(FormFields);
    }

    ngOnInit(): void {
        this._key = this.mode === Mode.MEMBER ? 'MEMBER' : 'MANAGER';

        this._storeSub = storeObserver<FormState>(
            this._store,
            (state) => storeStateMapper(state).form[this._key],
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        this.state = state;
                        this.stateToProps();
                    }),
                ),
            )
            .subscribe();

        this._formSub = this.form.form.valueChanges
            .pipe(
                debounceTime(500),
                tap((value) =>
                    this._store.dispatch(formValueChange(this._key, value)),
                ),
                debounceTime(5000),
                tap((value) => this._draftSubj.next(value)),
            )
            .subscribe();

        this._draftSubj = new Subject();
        this._draftSub = this._draftSubj
            .asObservable()
            .pipe(
                distinctUntilChanged(),
                filter(
                    () =>
                        ((this.isMember &&
                            this.payload.flowState ===
                                FlowState.MEMBER_DRAFT) ||
                            (this.isManager &&
                                (this.payload.flowState ===
                                    FlowState.MEMBER_SUBMITTED ||
                                    this.payload.flowState ===
                                        FlowState.MANAGER_DRAFT))) &&
                        !this._didSubmit &&
                        !this.state.pending,
                ),
                switchMap((value) =>
                    of(
                        this._formSave(value, {
                            isDraft: true,
                            team: this.isManager,
                        }),
                    ),
                ),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        if (this._draftSub) {
            this._draftSub.unsubscribe();
        }
        if (this._formSub) {
            this._formSub.unsubscribe();
        }
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
    }

    stateToProps(): void {
        const pl = this.state.payload || this.payload;
        this.payload = pl as any as PayloadView; // TODO revise
        this.payloadChange.emit(pl);

        // keep this separate from <member|manager>Can<Edit|Submit>
        this.isWithinCurrentDateRange = isWithinCurrentDateRange(
            this.payload.created as Date,
            this.reviewDateRanges,
        );

        this.isMember = this.mode === Mode.MEMBER;
        this.isManager = this.mode === Mode.MANAGER;
        this.isMemberSelf =
            this.isMember && this.info.userId === this._authService.user.id;

        this.memberCanEdit =
            pl.flowState === FlowState.MEMBER_DRAFT ||
            (pl.flowState === FlowState.MEMBER_REVIEW && !this.state.readonly);
        this.managerCanEdit =
            pl.flowState === FlowState.MEMBER_SUBMITTED ||
            pl.flowState === FlowState.MANAGER_DRAFT;
        // || pl.flowState === FlowState.MEMBER_REVIEW_SUBMITTED;

        this.memberCanSubmit =
            pl.flowState === FlowState.MEMBER_DRAFT ||
            pl.flowState === FlowState.MEMBER_REVIEW;
        // eslint-disable-next-line eqeqeq
        this.managerCanSubmit =
            // eslint-disable-next-line eqeqeq
            pl.flowState == FlowState.MEMBER_SUBMITTED ||
            pl.flowState === FlowState.MANAGER_DRAFT ||
            pl.flowState === FlowState.MEMBER_REVIEW_SUBMITTED;

        if (this.isMember && this.memberCanEdit && this.memberCanSubmit) {
            this.form.form.controls[this.form.fields.LOOKING_BACK.name].enable({
                emitEvent: false,
            });
            this.form.form.controls[
                this.form.fields.LOOKING_FORWARD.name
            ].enable({ emitEvent: false });
        } else {
            this.form.form.controls[this.form.fields.LOOKING_BACK.name].disable(
                { emitEvent: false },
            );
            this.form.form.controls[
                this.form.fields.LOOKING_FORWARD.name
            ].disable({ emitEvent: false });
        }

        if (this.isManager && this.managerCanEdit && this.managerCanSubmit) {
            this.form.form.controls[
                this.form.fields.CONVERSATION_SUMMARY.name
            ].enable({ emitEvent: false });
        } else {
            this.form.form.controls[
                this.form.fields.CONVERSATION_SUMMARY.name
            ].disable({ emitEvent: false });
        }

        let fv = this.state.formValue || pl || defaultFormValue();
        if (this.isMember) {
            fv = { ...fv, conversationSummary: pl.conversationSummary };
        }
        if (this.isManager) {
            fv = {
                ...fv,
                lookingBack: pl.lookingBack,
                lookingForward: pl.lookingForward,
            };
        }
        this.form.form.patchValue(
            {
                lookingBack: fv.lookingBack,
                lookingForward: fv.lookingForward,
                conversationSummary: fv.conversationSummary,
            },
            { emitEvent: false },
        );
        this._ifNotValidCheck();

        this._changeDetectorRef.detectChanges();
    }

    onSaveDraftClick(): void {
        this._draftSubj.next(this.form.form.value);
    }

    onReturnToMemberClick(): void {
        this._ngZone.run(() => {
            if (!this._ifNotValidCheck()) return;
            this._didSubmit = true;
            this._formSave(this.form.form.value, {
                isDraft: false,
                returnToMember: true,
                team: this.isManager,
            });
        });
    }

    onFinalEditClick(): void {
        this._store.dispatch(formSetReadonly(this._key, false));
    }

    onSubmit(): void {
        this._ngZone.run(() => {
            if (!this._ifNotValidCheck()) return;
            this._didSubmit = true;
            this._formSave(this.form.form.value, {
                isDraft: false,
                team: this.isManager,
            });
        });
    }

    private _ifNotValidCheck(): boolean {
        if (
            !this.form.form.valid &&
            !Object.values(this.form.fields).every(
                (f) => this.form.form.controls[f.name].disabled,
            )
        ) {
            this.form.checkWarningsErrors();
            this._changeDetectorRef.detectChanges();
            return false;
        }
        return true;
    }

    private _formSave(value: any, args: Args): any {
        this._store.dispatch(
            formSave(this._key, {
                payload: {
                    ...value,
                    threadGuid: this.payload.threadGuid,
                    memberId: this.isMember
                        ? this.info.userId
                        : this.payload.memberId,
                    managerId: this.isMember
                        ? this.info.manager.userId
                        : this.info.userId, // TODO revise
                },
                templateId: this.templateId,
                args,
            }),
        );
    }
}
